import React, { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useDate from '../../../../hooks/useDate/useDate';
import usePortalConfig from '../../../../hooks/usePortalConfig/usePortalConfig';
import useRolePermissions from '../../../../hooks/useRolePermissions/useRolePermissions';
import SimStatusTag from '../../../../components/StatusTag/SimStatusTag';
import TruncatedTooltip from '../../../../components/TruncatedTooltip/TruncatedTooltip';
import { Header, Row } from '../../../../components/Table/TableModels';
import { SimStatusSortPreference } from '../../../../configs/PortalConfig';
import { Sort } from '../../../../models/CommonModels';
import { Device, Sim, SimUpdateRequest } from '../../../../models/UserDevicesModels';
import DeviceDetailsSimRowEdit from './DeviceDetailsSimRowEdit';
import Tooltip from '../../../../components/Tooltip/Tooltip';

function useDeviceDetailsSimsTable(
  device: Device | null,
  sims: Sim[],
  sort: Sort,
  isPerformingAction: boolean,
  updateSim: (req: SimUpdateRequest, errorCallback: () => void) => void,
) {
  const [t] = useTranslation();
  const { localeDateFormat } = useDate();
  const { isUserAllowed } = useRolePermissions();
  const { routePermissions } = usePortalConfig();

  /**
   * Checks if the edit button can be rendered or not
   */
  const canRenderEdit = (sim: Sim) =>
    isUserAllowed(routePermissions.editDevices) &&
    sim.status !== 'PENDING' &&
    sim.status !== 'PROCESSING' &&
    !(sim.status === 'SUSPENDED' && sim.journey === 'TERMINATION');

  /**
   * Sorts the array of sims
   */
  const sortBy = useCallback(
    (list: Sim[]): Sim[] => {
      if (list.length) {
        if (!Object.keys(list[0]).includes(sort.orderBy)) {
          throw new Error(`Invalid field name: ${sort.orderBy}`);
        }
        type SortableKeys = Extract<
          'iccid' | 'eid' | 'model' | 'phoneNumber' | 'type' | 'startDate' | 'endDate' | 'status',
          keyof Sim
        >;
        return list.sort((a, b) => {
          let comparison = 0;
          if (sort.orderBy !== 'status') {
            try {
              comparison = a[sort.orderBy as SortableKeys].localeCompare(b[sort.orderBy as SortableKeys]);
            } catch (e) {
              comparison = 1;
            }
          } else {
            const indexA = SimStatusSortPreference.indexOf(a.status);
            const indexB = SimStatusSortPreference.indexOf(b.status);
            comparison = indexA - indexB;
          }
          return sort.order === 'desc' ? -comparison : comparison;
        });
      }
      return [];
    },
    [sort.order, sort.orderBy],
  );

  const headers: Header[] = useMemo(
    () => [
      {
        id: 'iccid',
        label: t('pages.userDetails.tabs.simsTable.headers.iccid'),
        isSortable: true,
      },
      {
        id: 'eid',
        label: t('pages.userDetails.tabs.simsTable.headers.eid'),
        isSortable: true,
      },
      {
        id: 'model',
        label: t('pages.userDetails.tabs.devicesTable.headers.device'),
        isSortable: true,
      },
      {
        id: 'phoneNumber',
        label: t('pages.userDetails.tabs.simsTable.headers.mobileNumber'),
        isSortable: true,
      },
      {
        id: 'type',
        label: t('translation:pages.userDetails.tabs.simsTable.headers.simType'),
        isSortable: true,
      },
      {
        id: 'imei',
        label: t('translation:pages.userDetails.tabs.simsTable.headers.imei'),
        isSortable: true,
      },
      {
        id: 'startDate',
        label: (
          <Tooltip
            label={t('pages.userDetails.tabs.simsTable.headers.startDate')}
            message={t('pages.userDetails.tabs.simsTable.tooltips.startDate')}
          />
        ),
        isSortable: true,
      },
      {
        id: 'endDate',
        label: (
          <Tooltip
            label={t('pages.userDetails.tabs.simsTable.headers.endDate')}
            message={t('pages.userDetails.tabs.simsTable.tooltips.endDate')}
          />
        ),
        isSortable: true,
      },
      {
        id: 'status',
        label: t('pages.userDetails.tabs.simsTable.headers.status'),
        isSortable: true,
      },
      {
        id: 'edit',
        label: '',
      },
    ],
    [t],
  );

  const rows: Row[] = useMemo(
    () =>
      sortBy(sims).map((s) => ({
        id: s.id,
        cells: [
          {
            value: s.iccid ? <TruncatedTooltip label={s.iccid} limit={4} /> : '',
          },
          {
            value: s.eid ? <TruncatedTooltip label={s.eid} limit={4} /> : '',
          },
          {
            value: device ? device.model : '',
          },
          {
            value: s.phoneNumber,
          },
          {
            value: s.type ? t(`common.simType.${s.type}`) : t('common.na'),
          },
          {
            value: s.imei ? <TruncatedTooltip label={s.imei} limit={4} /> : '',
          },
          {
            value: s.startDate ? DateTime.fromISO(s.startDate).toFormat(localeDateFormat) : '',
          },
          {
            value: s.endDate ? DateTime.fromISO(s.endDate).toFormat(localeDateFormat) : '',
          },
          {
            value: <SimStatusTag sim={s} label={t(`common.simStatus.${s.status}`)} />,
          },
          {
            value: canRenderEdit(s) ? (
              <Box sx={{ display: 'flex', minWidth: '25px', justifyContent: 'center' }}>
                <DeviceDetailsSimRowEdit sim={s} updateSim={updateSim} />
              </Box>
            ) : (
              <div />
            ),
          },
        ],
      })),
    [sims, sortBy, t, isPerformingAction],
  );

  return { headers, rows, totalSims: sims.length };
}

export default useDeviceDetailsSimsTable;
